/**
 * Visits panel Step B — one row per agent (labels-v2 PROPOSAL.md, founder
 * approved 2026-10-12). Same look as the Step A row, minus the per-record
 * lines (record id, policy):
 *
 *   ◈ GPTBot ✓                         ×412   12m ago
 *     OpenAI · top page /collections/summer
 *
 *   ┃ ⚙ “Googlebot” ⛨                  ×19     1h ago     (coral, own row)
 *   ☺ People · 1,204 visits                    2h ago
 *
 * Step C (labels-v2 PROPOSAL-C.md): unregistered bots are one row per bot
 * name. Founder feedback 2026-10-14 (the list read as a red alert): a row
 * gets the small, quiet "Probing" pill ONLY for meaningful probing
 * (`probeAlert`, ADR-0244: no registry name, 5+ probe requests, 25%+ of
 * its visits; or a registry-named row with 3+ universal probe requests).
 * No coral edge, and line 2 stays the normal roster line:
 *
 *   ⚙ Unknown bot · Mozilla/5.0  [⚠ Probing]   ×2,032   4m ago
 *     top page /
 *
 * One-record groups (orders, signed mandates, any non-OBSERVED decision)
 * render the Step A record row itself (`VisitRecordRow`).
 *
 * Identity marks follow Step A: Mismatch only from the server
 * `impersonation_reason`, Confirmed only from the server verdict (via
 * `identityState()`), never `cf_verified_bot`. A grouped row shows the
 * Confirmed mark when at least one record in range is Confirmed; the
 * tooltip says in how many of its records.
 */

import { identityState } from '@sill/audit';
import type { VisitGroup } from '@sill/types/api';

import type { SetView } from '../../app/view-router.js';
import { RelativeTime } from '../../components/atoms/index.js';
import { useC } from '../../lib/theme.js';
import { groupConfirmedNote, groupSecondLine, isPersonGroupKey, probeAlert, visitGroupName, visitMark } from './visit-identity.js';
import { ProbingPill } from './ProbingPill.js';
import { SecurityFlagChip } from './SecurityFlagChip.js';
import { UcpPlatformTrustMark } from './MandateDetailV3.js';
import { isUcpOrder } from './ucp.js';
import { VisitIdentityMark, VisitLogoGlyph } from './VisitIdentityMarks.js';
import { VisitRecordRow } from './VisitRecordRow.js';
import { VisitTrustChip } from './VisitTrustChip.js';
import type { MandateRecord } from './types.js';

const monoFont = "'JetBrains Mono', monospace";
const sansFont = "'Geist', sans-serif";

export interface VisitGroupRowProps {
  readonly group: VisitGroup;
  readonly isSelected: boolean;
  readonly isLast: boolean;
  readonly onSelect: () => void;
  /** ADR-0243 — the 'people' row's name under the People chip, next to the
   *  per-visitor cards ("Earlier visitors"). */
  readonly peopleLabel?: string;
  /** Founder bug 2026-10-20 — the SecurityFlagChip opens Security ›
   *  Attackers. Omitted in list surfaces that do not route (tests). */
  readonly setView?: SetView;
}

/** The name a grouped row (and its detail header) shows. */
export { visitGroupName } from './visit-identity.js';

export { ProbingPill } from './ProbingPill.js';

export function VisitGroupRow({ group, isSelected, isLast, onSelect, peopleLabel, setView }: VisitGroupRowProps): JSX.Element {
  const C = useC();
  if (group.kind === 'record') {
    return (
      <div data-group-key={group.group_key} data-group-kind="record">
        <VisitRecordRow
          record={group.representative as MandateRecord}
          isSelected={isSelected}
          isNew={false}
          isFlash={false}
          isLast={isLast}
          onSelect={onSelect}
        />
      </div>
    );
  }
  const rep = group.representative;
  const repResult = identityState(rep);
  const isMismatch = group.kind === 'mismatch';
  const isPeople = group.kind === 'people' || isPersonGroupKey(group.group_key);
  // The `people` row stands for every person in range: no single flag.
  const isAllPeople = isPeople && !isPersonGroupKey(group.group_key);
  const name = isAllPeople && peopleLabel !== undefined ? peopleLabel : visitGroupName(group);
  const example = group.kind === 'agent' ? group.confirmed_example : undefined;
  const exampleResult = example !== undefined ? identityState(example) : null;
  // Quiet pill only; the row keeps its normal edge and line 2.
  const probing = !isMismatch && probeAlert(group);
  const line2 = groupSecondLine(group);
  const edgeColor = isMismatch ? C.coralFg : isSelected ? C.limeFg : 'transparent';
  const visits = group.visits.toLocaleString('en-US');

  return (
    // A div, not a <button>: the trust chip inside has its own "Why?"
    // button, and buttons must not nest. Keyboard: Enter / Space.
    <div
      role="button"
      tabIndex={0}
      data-group-key={group.group_key}
      data-group-kind={group.kind}
      data-identity-state={isMismatch ? 'mismatch' : exampleResult?.state === 'confirmed' ? 'confirmed' : isPeople ? 'person' : 'unconfirmed'}
      data-probing={probing ? 'true' : undefined}
      onClick={onSelect}
      onKeyDown={(e) => {
        if (e.target !== e.currentTarget) return;
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onSelect(); }
      }}
      aria-pressed={isSelected}
      className="w-full p-4 text-left"
      style={{
        cursor: 'pointer',
        background: isSelected ? C.surface2 : 'transparent',
        borderLeft: `2px solid ${edgeColor}`,
        borderBottom: !isLast ? `1px solid ${C.borderSoft}` : 'none',
        transition: 'background-color 800ms ease-out, border-left-color 800ms ease-out',
      }}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2 min-w-0">
          {/* A confirmed visit in the row decides the logo, so Sill's own
              tools keep the Sill mark when any visit was proven. */}
          <VisitLogoGlyph
            record={example !== undefined && exampleResult !== null ? example : rep}
            result={example !== undefined && exampleResult !== null ? exampleResult : repResult}
            size={14}
            flag={!isAllPeople}
          />
          <span style={{ fontFamily: sansFont, fontSize: 13, color: C.text, fontWeight: 600, overflowWrap: 'anywhere' }}>
            {name}
          </span>
          {isPeople ? (
            <span style={{ fontFamily: sansFont, fontSize: 13, color: C.muted }}>
              · {visits} visit{group.visits === 1 ? '' : 's'}
            </span>
          ) : null}
          {isMismatch ? <VisitIdentityMark record={rep} result={repResult} /> : null}
          {example !== undefined && exampleResult !== null ? (
            <VisitIdentityMark record={example} result={exampleResult} note={groupConfirmedNote(group)} />
          ) : null}
          {probing ? <ProbingPill /> : null}
          {/* UCP order (founder 2026-10-21): the agent platform signed the
              request and the relay verified it before Sill accepted it, so
              the row shows the same mint shield-check the detail header
              does — fewer pills, same tooltip on hover/focus/tap. */}
          {isUcpOrder(rep) && !isMismatch && (example === undefined || exampleResult === null || visitMark(exampleResult) === null)
            ? <UcpPlatformTrustMark C={C} surface="row" /> : null}
          {/* Founder bug 2026-10-20 — Activity ↔ Security consistency.
              Prefer the confirmed example's flag if any (same precedence
              as the Confirmed mark above); otherwise the representative's. */}
          {(example?.security_flag ?? rep.security_flag) !== undefined ? (
            <SecurityFlagChip
              flag={(example?.security_flag ?? rep.security_flag) as NonNullable<typeof rep.security_flag>}
              {...(setView !== undefined ? { setView } : {})}
              surface="row"
            />
          ) : null}
          {group.kind === 'agent' || isMismatch ? (
            <VisitTrustChip
              record={example ?? rep}
              name={name}
              surface="row"
              mismatch={isMismatch}
              probing={probing}
            />
          ) : null}
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {!isPeople && group.visits > 1 ? (
            <span
              style={{ fontFamily: monoFont, fontSize: 10, color: C.skyFg, letterSpacing: 0.5 }}
              aria-label={`${visits} visits`}
            >
              ×{visits}
            </span>
          ) : null}
          <RelativeTime iso={group.last_seen} style={{ fontFamily: monoFont, fontSize: 9, color: C.muted }} />
        </div>
      </div>
      {line2 !== null ? (
        <div
          data-testid="visit-group-line2"
          style={{
            fontFamily: monoFont,
            fontSize: 10,
            color: C.muted,
            marginTop: 4,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {line2}
        </div>
      ) : null}
    </div>
  );
}
